// Canvas 8: Roadmap and Gates. Items pulled from Canvases 3 to 7 and the Evidence Gaps list are sorted into three phases.
// A gate ends each phase and releases the next tranche. Check 5 (the Gate check) runs gate by gate.
import { makeDef, G, text, long, num, pick, tag, calc, fields, table, pull, reference } from './base.js';
import { OPT } from '../spine.js';
import { has, S, N, T, rows, roundHalfUp, gap, sortGaps } from '../helpers.js';

// ---------------------------------------------------------------- content
const PHASES = [
  { no: 1, name: 'Mobilise and prove', window: '0 to 6 months' },
  { no: 2, name: 'Build and stabilise', window: '6 to 12 months' },
  { no: 3, name: 'Scale and embed', window: '12 to 18 months' },
];
const LANES = [
  { v: 'Use cases', label: 'Use cases', hint: 'from Canvas 3' },
  { v: 'Capability and people', label: 'Capability and people', hint: 'from Canvas 7' },
  { v: 'Data and platform', label: 'Data and platform', hint: 'from Canvas 5 and the Evidence Gaps list' },
  { v: 'Governance', label: 'Governance', hint: 'from Canvas 6' },
];
const TRANCHE_ROWS = PHASES.map((p) => ({ id: `p${p.no}`, label: `Phase ${p.no}: ${p.name}`, sub: p.window }));
const GATE_ROWS = [
  { id: 'g1', label: 'Gate 1: the end of Phase 1', sub: 'This gate releases the Phase 2 tranche.' },
  { id: 'g2', label: 'Gate 2: the end of Phase 2', sub: 'This gate releases the Phase 3 tranche.' },
  { id: 'g3', label: 'Gate 3: the end of Phase 3', sub: 'This gate releases the decision to continue beyond Phase 3.' },
];
const VALUE_ROWS = [
  ['customer', 'Customer'],
  ['business', 'Business'],
  ['efficiency', 'AI and efficiency'],
  ['trust', 'Trust and Human'],
];
const AREAS = {
  principles: 'Responsible AI principles',
  data: 'Data governance',
  handover: 'Human handover design',
  roles: 'Roles and decision forums',
};
const CHECK5_HEAD = ['If this gate cell is empty', 'Go back to', 'Why'];
const CHECK5_ROWS = [
  ['Value measure', 'Canvas 4', 'The measure that shows a phase worked comes from the value rows.'],
  ['Launch condition', 'Canvas 6', 'The launch conditions are the output of the guardrails canvas.'],
  ['Readiness measure', 'Canvas 7 or 5', 'People rehearsed (Canvas 7), or data and owners ready (Canvas 5).'],
  ['Decider', 'Canvas 5', 'The operating model says who decides. Name one role.'],
];

// ---------------------------------------------------------------- helpers (local to this file)
const fx = (up, n) => (up && up[n] && up[n].d && up[n].d.facts) || {};
const isNum = (x) => typeof x === 'number' && Number.isFinite(x);
const arr = (x) => (Array.isArray(x) ? x : []);
const lines = (list) => (list.length ? list.join('\n') : null);
// millions with at least one decimal and no trailing zero beyond two: 600000 -> "0.6", 1000000 -> "1.0", 150000 -> "0.15"
const mil = (x) => {
  const s = String(roundHalfUp(x / 1e6, 2));
  return s.includes('.') ? s : `${s}.0`;
};
const money = (x, cur) => `${cur}${mil(x)}M`;
const range = (lo, hi, cur) => `${money(lo, cur)} to ${money(hi, cur)}`;
const the = (s) => (/^the\s/i.test(s) ? s : `the ${s}`);
const withThe = (s) => (/^(CFO|CEO|COO|CTO|CIO|CRO)$/i.test(s) ? `The ${s}` : s);
// one named role: not a committee, a board, a team or two people (the workbook rule). Roles that contain "and" are allowed.
const KNOWN_AND = /IT and Data|Risk and Compliance|People and Governance|Data and AI/gi;
const oneRole = (s) => !/\b(committee|board|team)\b/i.test(s) && !/ and /i.test(s.replace(KNOWN_AND, ''));
// the Evidence Gaps list, G first then E (sorting again is harmless: the sort is stable), and its first five
const topFive = (up) => sortGaps(arr(up && up.agg && up.agg.gaps)).slice(0, 5);
const countIn = (v, n) => rows(v, `ph${n}`).filter((r) => r.item).length;

// ---------------------------------------------------------------- what step 1 and the other pull blocks show
const picksText = (up) => {
  const f3 = fx(up, 3);
  const top = f3.topPick;
  const second = f3.secondPick;
  if (!top || !top.name) return null;
  const out = [`Top pick: ${top.name}${top.flag ? ' (guardrail flag)' : ''}`];
  if (second && second.name) out.push(`Second pick: ${second.name}${second.flag ? ' (guardrail flag)' : ''}`);
  return lines(out);
};
const costText = (up) => {
  const f4 = fx(up, 4);
  const inv = f4.invest || {};
  const cur = f4.cur || 'S$';
  const out = [];
  if (isNum(inv.oneOffLow) && isNum(inv.oneOffHigh)) out.push(`One-off ${mil(inv.oneOffLow)} to ${mil(inv.oneOffHigh)} ${cur}M.`);
  if (isNum(inv.runLow) && isNum(inv.runHigh)) out.push(`Run ${mil(inv.runLow)} to ${mil(inv.runHigh)} ${cur}M a year.`);
  if (isNum(inv.contingencyPct)) out.push(`Contingency ${inv.contingencyPct}%${inv.contingencyHolder ? `, held by ${the(inv.contingencyHolder)}` : ''}.`);
  return out.length ? out.join(' ') : null;
};
const baselinesToMeasure = (up) => {
  const r = fx(up, 4).rows || {};
  const out = [];
  VALUE_ROWS.forEach(([k]) => {
    const x = r[k];
    if (x && x.measure && x.tag !== 'M') out.push(`${x.measure} (${x.tag || 'no tag'}; baseline: ${x.baseline || 'blank'})`);
  });
  return lines(out);
};
const valueMeasures = (up) => {
  const r = fx(up, 4).rows || {};
  const out = [];
  VALUE_ROWS.forEach(([k, label]) => {
    const x = r[k];
    if (x && x.measure) out.push(`${label}: ${x.measure}${x.change ? `, change ${x.change}` : ''}`);
  });
  return lines(out);
};
const ownerlessData = (up) =>
  lines(
    arr(fx(up, 5).dataItems)
      .filter((d) => d.item && !d.owner)
      .map((d) => `${d.item}${d.source ? ` (${d.source})` : ''}`),
  );
const dataNotReady = (up) =>
  lines(
    arr(fx(up, 5).dataItems)
      .filter((d) => d.item && d.ready && d.ready !== 'Yes')
      .map((d) => `${d.item}: ready ${d.ready}${d.owner ? ` (owner: ${d.owner})` : ' (no owner)'}`),
  );
const riskiestGap = (up) => {
  const f6 = fx(up, 6);
  return f6.riskiestGap ? `${f6.riskiestGap}${f6.gapOwner ? ` (owner: ${f6.gapOwner})` : ''}` : null;
};
const launchConditions = (up) => lines(arr(fx(up, 6).launchConditions).filter(Boolean).map((c, i) => `${i + 1}. ${c}`));
const areasToClose = (up) =>
  lines(
    arr(fx(up, 6).areas)
      .filter((a) => a.rating && a.rating !== 'In place')
      .map((a) => `${AREAS[a.key] || a.key}: ${a.rating}${a.action ? `. Action: ${a.action}` : ''}${a.owner ? ` (owner: ${a.owner})` : ''}`),
  );
const trainingRoles = (up) =>
  lines(
    arr(fx(up, 7).roles)
      .filter((r) => r.role)
      .map((r) => `${r.role}: ${r.lever || 'no lever yet'}${r.owner ? ` (owner: ${r.owner})` : ''}`),
  );
const rehearsals = (up) =>
  lines(
    arr(fx(up, 7).roles)
      .filter((r) => r.role && r.rehearsal)
      .map((r) => `${r.rehearsal} (${r.role})`),
  );
const gapLines = (up) => lines(topFive(up).map((g) => `${g.tag}: ${g.item} (${g.where})`));
const contingencyPull = (up) => {
  const pct = fx(up, 4).invest && fx(up, 4).invest.contingencyPct;
  return isNum(pct) ? `${pct}%` : null;
};

// ---------------------------------------------------------------- columns
const itemCols = (ex) => [
  pick('lane', 'Lane', LANES, {
    required: true,
    w: 1,
    guide: G(
      'Pick the lane the item belongs to: use cases, capability and people, data and platform, or governance.',
      ex.lane,
      'Use cases come from Canvas 3, capability and people from Canvas 7, data and platform from Canvas 5 and the Evidence Gaps list, governance from Canvas 6.',
      'Putting every item under use cases. The items from Canvases 5, 6 and 7 are what make a phase safe to start.',
    ),
  }),
  text('item', 'Item', {
    required: true,
    w: 2,
    placeholder: 'Verb first',
    guide: G(
      'Write one item to a row, verb first. Add the month if it has one.',
      ex.item,
      'Phase 1 holds the launch conditions, the Phase 1 dependencies, the first rehearsals and a measurement task for every Guess.',
      'A topic is not an item. "Data" is a topic; "Name owners for the two ownerless data items" is an item.',
    ),
  }),
  text('owner', 'Owner (a role)', {
    required: true,
    w: 1,
    guide: G(
      'Write the one role who does the item or answers for it. No owner, no slot.',
      ex.owner,
      undefined,
      'A team or a project name. Write one role.',
    ),
  }),
];
const P1_EX = {
  lane: 'Data and platform',
  item: 'Measure the four Canvas 4 baselines (months 1 to 2)',
  owner: 'Head of Retail Banking',
};
const P2_EX = {
  lane: 'Use cases',
  item: 'Build Decline reason and review behind its Tier 3 controls',
  owner: 'Head of IT and Data',
};
const P3_EX = {
  lane: 'Use cases',
  item: 'Extend pre-fill to all 140 branches',
  owner: 'Head of Retail Banking',
};

const gateNeeded = (v, row, rowInfo) => {
  const n = parseInt(String((rowInfo && rowInfo.id) || 'g1').replace(/\D+/g, ''), 10) || 1;
  if (n === 1) return true;
  if (countIn(v, n) > 0) return true;
  return Object.values(row || {}).some((x) => String(x || '').trim() !== '');
};
const trNeeded = ({ row, rowInfo }) => !rowInfo || rowInfo.id !== 'p3' || has(row.low) || has(row.high) || has(row.tag);

// ---------------------------------------------------------------- NorthStar (master spec, section 6; workbook ns_data.roadmap)
const NORTHSTAR = {
  ...T('ph1', [
    { lane: 'Use cases', item: 'Pilot pre-fill and document reading in one region (months 3 to 6)', owner: 'Head of CX' },
    { lane: 'Capability and people', item: 'Train two CX product owners', owner: 'Head of CX' },
    { lane: 'Capability and people', item: 'Train the pilot-region branch staff', owner: 'Head of Retail Banking' },
    { lane: 'Data and platform', item: 'Measure the four Canvas 4 baselines (months 1 to 2)', owner: 'Head of Retail Banking' },
    { lane: 'Data and platform', item: 'Name owners for the two ownerless data items', owner: 'Head of IT and Data' },
    { lane: 'Data and platform', item: 'Start unifying the customer profile', owner: 'Head of IT and Data' },
    { lane: 'Governance', item: 'Set up the AI decision forum', owner: 'Head of Retail Banking' },
    { lane: 'Governance', item: 'Name the owner of explanation and appeal', owner: 'Head of Risk' },
  ]),
  ...T('ph2', [
    { lane: 'Use cases', item: 'Extend pre-fill to three regions', owner: 'Head of CX' },
    { lane: 'Use cases', item: 'Build Decline reason and review behind its Tier 3 controls', owner: 'Head of IT and Data' },
    { lane: 'Capability and people', item: 'Rehearse the decline handover with service officers', owner: 'Head of Retail Banking' },
  ]),
  ...T('ph3', [
    { lane: 'Use cases', item: 'Extend pre-fill to all 140 branches', owner: 'Head of Retail Banking' },
    { lane: 'Use cases', item: 'Add the guided upload assistant', owner: 'Head of CX' },
  ]),
  ...T(
    'later',
    [{ trigger: 'It sits in Phase 3 already' }, { trigger: 'Regulatory approval in each market' }, { trigger: 'An owner for the churn model data' }],
    ['r2', 'r3', 'r5'],
  ),
  ...T('tr', [{ low: '200000', high: '300000', tag: 'E' }, { low: '400000', high: '700000', tag: 'E' }, {}], ['p1', 'p2', 'p3']),
  ...T(
    'gate',
    [
      {
        value: 'Completion in the pilot region at or above the measured baseline plus 5 points',
        launch: 'An owner for explanation and appeal is named and the forum has met',
        ready: 'Two CX product owners trained and pilot branches rehearsed',
        decider: 'CFO',
      },
      {
        value: 'The completion gain holds across three regions',
        launch: 'Tier 3 controls in place for use case 4, including independent validation and a live human review path',
        ready: 'Service officers rehearsed on the three handover states',
        decider: 'Head of Retail Banking',
      },
      {
        value: 'Complaint resolution for decline complaints at or under 3 business days',
        launch: 'The forum reviews both use cases each quarter and the incident path has been tested',
        ready: 'All branches trained',
        decider: 'Chief Executive',
      },
    ],
    ['g1', 'g2', 'g3'],
  ),
  ...T('risk', [
    { risk: 'The baseline is not measured by month 2', trigger: 'No figure by week 8', owner: 'Head of Retail Banking', response: 'Hold the pilot start' },
    { risk: 'The ownerless data items stay ownerless', trigger: 'No name by month 1', owner: 'Head of IT and Data', response: 'Escalate to the sponsor' },
    {
      risk: 'Branch staff do not use pre-fill',
      trigger: 'Use below half of applications by month 4',
      owner: 'Head of Retail Banking',
      response: 'Run the rehearsal again and ask what is not useful or not easy',
    },
  ]),
  ...T('tick', [{ p1: 'Yes' }, { p1: 'Yes' }, { p1: 'Yes' }, { p1: 'Yes' }], ['t1', 't2', 't3', 't4']),
  'cont.holder': 'CFO',
  'cont.why': 'The cost lines are estimates',
  'cont.tag': 'E',
  'so.reviewer': 'CFO',
  'so.objection': 'What evidence releases the money for Phase 2?',
  'so.evidence': 'Gate 1: three measures and one decider: Canvas 8 gate row',
  'so.owner': 'CFO',
  'so.status': 'Answered',
};

export default makeDef(8, {
  short: 'Roadmap',
  purpose:
    'Put the work in order: three phases, a gate at the end of each, and one named decider for every gate, so that evidence releases each tranche of money.',
  howTo: [
    'Read what Canvases 3 to 7 and the Evidence Gaps list hand over, then place each item in a phase. No owner, no slot.',
    'Write a gate at the end of every phase: a value measure, a launch condition, a readiness measure and one named decider.',
    'Name the three delivery risks that could stop the plan, then check that every Guess in your top five has a Phase 1 item.',
    'Check 5 runs gate by gate. A Go back sends you to the canvas that holds the missing cell.',
  ],
  results: [
    { id: 'items', label: 'Items in Phases 1, 2 and 3', rule: 'Count the items in each phase.' },
    { id: 'gates', label: 'Phases with a full gate', rule: 'Count the gates that pass Check 5: all four cells written.' },
    { id: 'noOwner', label: 'Items without an owner', rule: 'Count the items with no owner beside them.' },
    { id: 'risks', label: 'Risks fully written', rule: 'Count the risks that have a risk, a trigger, an owner and a first response.' },
    {
      id: 'guesses',
      label: 'Guesses in the top five with a Phase 1 item',
      rule: 'Count the Guesses in the Evidence Gaps top five that you ticked "Phase 1 item", out of all the Guesses in it.',
    },
    {
      id: 'check5',
      label: 'Check 5: Gate check',
      rule: 'Pass: every gate has a value measure, a launch condition, a readiness measure and one named decider. Go back: fix the cell named.',
    },
  ],
  steps: [
    {
      hint:
        'Read what your earlier canvases hand over: the picks, the cost, the baselines to measure, the data items with no owner, ' +
        'the launch conditions, the training roles and the first five on the Evidence Gaps list. Nothing to type here.',
      blocks: [
        pull('pull1', 'Pulled from your pack', [
          { id: 'picks', label: 'Top pick and second pick', ref: 'Canvas 3', no: 3, get: picksText },
          { id: 'sponsor', label: 'Sponsor', ref: 'Case Card C', no: 0, get: (up) => fx(up, 0).sponsor || null },
          { id: 'cost', label: 'Cost and contingency', ref: 'Canvas 4', no: 4, get: costText },
          {
            id: 'measure',
            label: 'Baselines to measure',
            ref: 'Canvas 4',
            no: 4,
            get: baselinesToMeasure,
            note: 'Each one needs a measurement task in Phase 1.',
          },
          {
            id: 'data',
            label: 'Data items with no owner',
            ref: 'Canvas 5',
            no: 5,
            get: ownerlessData,
            note: 'Each one is a Phase 1 dependency.',
          },
          { id: 'launch', label: 'Riskiest gap', ref: 'Canvas 6', no: 6, get: (up) => riskiestGap(up) },
          { id: 'conditions', label: 'Launch conditions', ref: 'Canvas 6', no: 6, get: launchConditions },
          { id: 'areas', label: 'Guardrail areas still to close', ref: 'Canvas 6', no: 6, get: areasToClose },
          { id: 'roles', label: 'Roles to train, and the lever for each', ref: 'Canvas 7', no: 7, get: trainingRoles },
          { id: 'rehearsals', label: 'First rehearsals', ref: 'Canvas 7', no: 7, get: rehearsals },
          {
            id: 'gaps',
            label: 'Evidence Gaps: the first five',
            ref: 'Evidence Gaps list',
            get: gapLines,
            note: 'G first, then E. Step 5 asks which Guesses have a Phase 1 item.',
          },
        ]),
      ],
    },
    {
      hint:
        'Place each item in a phase and a lane, verb first, with its owner beside it. No owner, no slot. ' +
        'Phase 1 holds the launch conditions, the Phase 1 dependencies, the first rehearsals and a measurement task for every Guess.',
      blocks: [
        table('ph1', {
          title: 'Phase 1: Mobilise and prove, 0 to 6 months',
          rowLabel: 'Item',
          rows: { min: 1, max: 12, start: 4, label: 'Item' },
          pdf: 'table',
          columns: itemCols(P1_EX),
        }),
        table('ph2', {
          title: 'Phase 2: Build and stabilise, 6 to 12 months',
          rowLabel: 'Item',
          rows: { min: 0, max: 12, start: 3, label: 'Item' },
          pdf: 'table',
          columns: itemCols(P2_EX),
        }),
        table('ph3', {
          title: 'Phase 3: Scale and embed, 12 to 18 months',
          rowLabel: 'Item',
          rows: { min: 0, max: 12, start: 2, label: 'Item' },
          pdf: 'table',
          columns: itemCols(P3_EX),
        }),
        table('later', {
          title: 'Later list: the use cases not in your top two',
          rowLabel: 'Use case',
          rows: {
            from: (up) => {
              const f3 = fx(up, 3);
              if (!f3.topPick || !f3.topPick.name) return [];
              return arr(f3.candidates)
                .filter((c) => c.name && c.pick !== 'top' && c.pick !== 'second')
                .map((c) => ({ id: c.id, label: c.name, sub: c.stage || undefined }));
            },
          },
          pdf: 'table',
          columns: [
            text('trigger', 'Trigger to bring it in', {
              required: true,
              w: 4,
              guide: G(
                'Write what would have to be true for this use case to enter the roadmap. If it already sits in a phase, say which.',
                'Regulatory approval in each market',
                'One row for every use case that is not your top pick or second pick.',
                'A date with no condition. A trigger is something you can see happen.',
              ),
            }),
          ],
        }),
      ],
    },
    {
      hint:
        'No gate, no phase. One named role decides: not a committee, a board, a team or two people. ' +
        'The tranche a gate releases sits at the top of the next phase. Check 5: Pass when all four cells are written; if one is empty, go back to its canvas.',
      blocks: [
        pull('pull3', 'What each gate cell draws on', [
          { id: 'values', label: 'Value measures', ref: 'Canvas 4', no: 4, get: valueMeasures },
          { id: 'conditions', label: 'Launch conditions', ref: 'Canvas 6', no: 6, get: launchConditions },
          { id: 'people', label: 'Readiness: people to rehearse', ref: 'Canvas 7', no: 7, get: (up) => rehearsals(up) || trainingRoles(up) },
          { id: 'datas', label: 'Readiness: data not yet ready', ref: 'Canvas 5', no: 5, get: dataNotReady },
        ]),
        table('tr', {
          title: 'Tranche for each phase',
          rowLabel: 'Phase',
          rows: { fixed: TRANCHE_ROWS },
          pdf: 'table',
          columns: [
            num('low', 'Low', {
              required: trNeeded,
              w: 1,
              min: 0,
              placeholder: 'Full amount',
              guide: G(
                'Write the lowest money this phase needs, as a full amount in the Canvas 4 currency. The gate before it releases it.',
                '200000 (S$0.2M) for Phase 1',
                'The tranches should add up to the Canvas 4 one-off cost. Phase 3 can wait for Phase 2: its figure is estimated then.',
                'Typing millions (0.2). Type the full amount, 200000.',
              ),
            }),
            num('high', 'High', {
              required: trNeeded,
              w: 1,
              min: 0,
              placeholder: 'Full amount',
              guide: G(
                'Write the highest money this phase needs, as a full amount in the Canvas 4 currency.',
                '300000 (S$0.3M) for Phase 1',
                undefined,
                'A high figure below the low figure.',
              ),
            }),
            tag('tag', 'Tag', {
              required: trNeeded,
              w: 1,
              guide: G(
                'M for a quoted or contracted figure, E for an estimate with a stated basis, G for a guess.',
                'E',
                'A tranche tagged E or G goes to the Evidence Gaps list.',
                'Leaving Phase 3 blank is fine. Do not invent a figure for it.',
              ),
            }),
          ],
        }),
        table('gate', {
          title: 'Gates',
          rowLabel: 'Gate',
          rows: { fixed: GATE_ROWS },
          pdf: 'cards',
          columns: [
            long('value', 'Value measure (from Canvas 4)', {
              required: ({ v, row, rowInfo }) => gateNeeded(v, row, rowInfo),
              w: 2,
              rows: 2,
              guide: G(
                'Write one measure from Canvas 4 that shows the phase worked, with the level that must be reached.',
                'Completion in the pilot region at or above the measured baseline plus 5 points',
                'A value measure has a number or a level that can be passed or failed.',
                '"Improve completion" cannot pass or fail. Name the level.',
              ),
            }),
            long('launch', 'Launch condition (from Canvas 6)', {
              required: ({ v, row, rowInfo }) => gateNeeded(v, row, rowInfo),
              w: 2,
              rows: 2,
              guide: G(
                'Write one launch condition from Canvas 6 that must be in place before the next phase starts.',
                'An owner for explanation and appeal is named and the forum has met',
                undefined,
                'A control you have not named on Canvas 6. Go back and add it there first.',
              ),
            }),
            long('ready', 'Readiness measure (from Canvas 7 or 5)', {
              required: ({ v, row, rowInfo }) => gateNeeded(v, row, rowInfo),
              w: 2,
              rows: 2,
              guide: G(
                'Write one measure from Canvas 7 (people rehearsed) or Canvas 5 (data and owners ready).',
                'Two CX product owners trained and pilot branches rehearsed',
                undefined,
                'Training delivered is not readiness. Say what people can now do, or what is now owned.',
              ),
            }),
            text('decider', 'Decider (one named role)', {
              required: ({ v, row, rowInfo }) => gateNeeded(v, row, rowInfo),
              w: 2,
              guide: G(
                'Write the one role who decides whether the gate is passed and the next tranche is released.',
                'CFO',
                'One named role decides: not a committee, a board, a team or two people.',
                'Two names joined by "and". If two people must agree, name the one who decides if they do not.',
              ),
            }),
            calc('rel', 'This gate releases', {
              w: 2,
              rule: 'the next phase tranche; Gate 3 releases the decision to continue beyond Phase 3',
              guide: G(
                'Worked out for you from the tranche table above.',
                'The Phase 2 tranche, S$0.4M to S$0.7M',
                'Gate 1 releases the Phase 2 tranche, Gate 2 the Phase 3 tranche, Gate 3 the decision to continue.',
              ),
            }),
            calc('chk', 'Check 5', {
              w: 2,
              rule: 'Pass when all four cells are written; if one is empty, it names the canvas to go back to',
              guide: G('Worked out for you from the four cells.', 'Pass', 'Pass when the value measure, launch condition, readiness measure and decider are all written.'),
            }),
          ],
        }),
        fields([
          calc('c5.say', 'Check 5 says', {
            w: 4,
            rule: 'the first gate that fails, or a phase that has items and no gate',
            guide: G(
              'Worked out for you. It names the first gate that fails, and the canvas to go back to.',
              'Pass',
              'A failed check sends you upstream to the source canvas. Never change the roadmap to fit.',
            ),
          }),
        ]),
        reference('Check 5: where to go back', CHECK5_HEAD, CHECK5_ROWS),
      ],
    },
    {
      hint:
        'List the three delivery risks that could stop the plan, riskiest gap first. Give each a trigger (a date or a number), an owner and a first response.',
      blocks: [
        pull('pull4', 'Pulled from your pack', [
          { id: 'riskiest', label: 'Riskiest gap and its owner', ref: 'Canvas 6', no: 6, get: (up) => riskiestGap(up) },
          { id: 'noowner', label: 'Data items with no owner', ref: 'Canvas 5', no: 5, get: ownerlessData },
        ]),
        table('risk', {
          title: 'Top three delivery risks',
          rowLabel: 'Risk',
          rows: { min: 3, max: 3, start: 3, label: 'Risk' },
          pdf: 'cards',
          columns: [
            text('risk', 'Risk', {
              required: true,
              w: 2,
              guide: G(
                'Write what could stop the plan: a slipped dependency, a missing owner, low use. Start with the riskiest gap from Canvas 6.',
                'The baseline is not measured by month 2',
                'Ask which dependency, if it slips, moves everything.',
                'A risk everyone shares, such as "budget". Name the one that stalls Phase 2.',
              ),
            }),
            text('trigger', 'Trigger', {
              required: true,
              w: 2,
              guide: G(
                'Write the sign that tells you the risk has arrived, with a date or a number.',
                'No figure by week 8',
                undefined,
                '"If it goes badly" is not a trigger. Name a date or a number.',
              ),
            }),
            text('owner', 'Owner (a role)', {
              required: true,
              w: 2,
              guide: G('Write the one role who watches for the trigger.', 'Head of Retail Banking', undefined, 'A team. Write one role.'),
            }),
            text('response', 'First response', {
              required: true,
              w: 2,
              guide: G(
                'Write the first thing the owner does when the trigger fires.',
                'Hold the pilot start',
                undefined,
                'A plan to "monitor". Write an action.',
              ),
            }),
          ],
        }),
      ],
    },
    {
      hint:
        'Tick each Guess in the Evidence Gaps top five that has a Phase 1 item to measure it. Then write who holds the contingency and why, ' +
        'and what you stop to make room.',
      blocks: [
        pull('pull5', 'From Canvas 4', [
          { id: 'contingency', label: 'Contingency', ref: 'Canvas 4', no: 4, get: contingencyPull },
          { id: 'cost', label: 'Cost', ref: 'Canvas 4', no: 4, get: costText },
        ]),
        table('tick', {
          title: 'Evidence Gaps: the first five',
          rowLabel: 'Number',
          rows: {
            from: (up) => topFive(up).map((g, i) => ({ id: `t${i + 1}`, label: g.item, sub: `${g.tag}, ${g.where}` })),
          },
          pdf: 'table',
          columns: [
            pick('p1', 'Phase 1 item? (Yes)', OPT.yes, {
              w: 1,
              guide: G(
                'Tick Yes when Phase 1 holds an item that measures this number. Every Guess needs one.',
                'Yes, for each of the four Canvas 4 baselines',
                'Every Guess in the top five needs a Phase 1 item.',
                'Ticking a Guess whose measurement task is not on the page. Write the item in step 2 first.',
              ),
            }),
          ],
        }),
        fields([
          text('cont.holder', 'Contingency held by (a role)', {
            required: true,
            w: 2,
            guide: G(
              'Write the role who holds the contingency and decides when it is spent. Canvas 4 gives the percentage.',
              'CFO',
              'The CFO will ask for the cost range with contingency, and what stops.',
              'A contingency nobody holds. Name one role.',
            ),
          }),
          tag('cont.tag', 'Tag for the contingency', {
            required: true,
            w: 1,
            guide: G(
              'M if it is a rule you can show, E if you estimated it with a stated basis, G if it is a guess.',
              'E',
              'A contingency tagged E or G goes to the Evidence Gaps list.',
            ),
          }),
          long('cont.why', 'Why this contingency', {
            w: 4,
            rows: 2,
            guide: G(
              'Say why this percentage, in one line.',
              'The cost lines are estimates',
              undefined,
              'A figure with no reason. The CFO will ask why.',
            ),
          }),
          long('stop', 'What we stop to make room', {
            w: 4,
            rows: 2,
            placeholder: 'For example: no new AI pilots start outside this plan until Gate 1',
            guide: G(
              'Name the work that stops or waits so that Phase 1 has the people and the time it needs.',
              'No new AI pilots start outside this plan until Gate 1 (a model answer: NorthStar leaves this for the room to decide)',
              'The CFO asks "what stops?". An honest blank is a finding.',
              'Adding Phase 1 on top of a full diary. Something has to give.',
            ),
          }),
          calc('tranches', 'Tranches entered, against Canvas 4', {
            w: 4,
            rule: 'add the lows, add the highs, then set them beside the Canvas 4 one-off cost',
            guide: G(
              'Worked out for you from the tranche table.',
              '0.6 to 1.0 S$M in tranches; one-off cost 0.6 to 1.0',
              'The lows add to the low one-off cost and the highs to the high one-off cost, until Phase 3 is estimated.',
            ),
          }),
        ]),
      ],
    },
  ],
  derive(v, up) {
    const f4 = fx(up, 4);
    const cur = f4.cur || 'S$';
    const inv = f4.invest || {};

    // items per phase
    const items = PHASES.map((ph) => rows(v, `ph${ph.no}`).filter((r) => r.item));
    const counts = items.map((a) => a.length);
    const total = counts[0] + counts[1] + counts[2];
    const noOwner = items.reduce((n, a) => n + a.filter((r) => !r.owner).length, 0);

    // tranches
    const tr = PHASES.map((ph) => ({
      no: ph.no,
      low: N(v, `tr.p${ph.no}.low`),
      high: N(v, `tr.p${ph.no}.high`),
      tag: S(v, `tr.p${ph.no}.tag`),
    }));
    const trText = (t) => (t.low !== null && t.high !== null ? range(t.low, t.high, cur) : '');

    // gates
    const gates = [1, 2, 3].map((n) => {
      const g = {
        value: S(v, `gate.g${n}.value`),
        launch: S(v, `gate.g${n}.launch`),
        ready: S(v, `gate.g${n}.ready`),
        decider: S(v, `gate.g${n}.decider`),
      };
      g.any = !!(g.value || g.launch || g.ready || g.decider);
      let chk = '';
      if (g.any) {
        if (!g.value) chk = 'Go back: Canvas 4. This gate has no value measure';
        else if (!g.launch) chk = 'Go back: Canvas 6. This gate has no launch condition';
        else if (!g.ready) chk = 'Go back: Canvas 7 or 5. This gate has no readiness measure';
        else if (!g.decider) chk = 'Go back: Canvas 5. This gate has no decider: name one role';
        else chk = 'Pass';
      }
      g.chk = chk;
      return g;
    });
    const releases = (n) => {
      const g = gates[n - 1];
      if (n === 3) return g.any || counts[2] > 0 ? 'The decision to continue beyond Phase 3' : '';
      if (!g.any) return '';
      return `The Phase ${n + 1} tranche, ${trText(tr[n]) || 'still to estimate'}`;
    };

    // Check 5, overall (the workbook's cell F32)
    let check5Say = '';
    if (gates.some((g) => g.any)) {
      const failed = gates.findIndex((g) => g.chk.startsWith('Go back'));
      const noGate = gates.findIndex((g, i) => counts[i] > 0 && g.chk === '');
      if (failed >= 0) check5Say = gates[failed].chk.replace('This gate', `Gate ${failed + 1}`);
      else if (noGate >= 0) {
        check5Say = `Go back: Canvases 4, 6 and 7. Phase ${noGate + 1} has items and no gate: write Gate ${noGate + 1} from them (no gate, no phase)`;
      } else if (gates[0].chk === 'Pass') check5Say = 'Pass';
    }
    const fullGates = gates.filter((g) => g.chk === 'Pass').length;

    // risks
    const riskRows = rows(v, 'risk');
    const risksFull = riskRows.filter((r) => r.risk && r.trigger && r.owner && r.response).length;

    // the Evidence Gaps top five and the Phase 1 ticks
    const top5 = topFive(up);
    const gCount = top5.filter((g) => g.tag === 'G').length;
    const ticked = top5.filter((g, i) => g.tag === 'G' && S(v, `tick.t${i + 1}.p1`) === 'Yes').length;
    let guesses = '';
    if (top5.some((g) => g.tag)) guesses = gCount === 0 ? 'No Guess in the top five' : `${ticked} of ${gCount}`;

    // tranches against Canvas 4
    const withNums = tr.filter((t) => t.low !== null || t.high !== null);
    const lows = tr.reduce((a, t) => a + (t.low || 0), 0);
    const highs = tr.reduce((a, t) => a + (t.high || 0), 0);
    let tranches = '';
    if (withNums.length) {
      tranches = `${mil(lows)} to ${mil(highs)} ${cur}M in tranches`;
      if (isNum(inv.oneOffLow) && isNum(inv.oneOffHigh)) tranches += `; one-off cost ${mil(inv.oneOffLow)} to ${mil(inv.oneOffHigh)}`;
    }

    // contingency
    const pct = isNum(inv.contingencyPct) ? `${roundHalfUp(inv.contingencyPct, 1)}%` : '';
    const holder = S(v, 'cont.holder') || String(inv.contingencyHolder || '').trim();
    const contingency = [pct, holder ? `held by ${the(holder)}` : ''].filter(Boolean).join(', ');

    const out = { calc: {}, results: {}, evidence: [], warnings: [], blockers: [] };
    gates.forEach((g, i) => {
      out.calc[`gate.g${i + 1}.rel`] = releases(i + 1);
      out.calc[`gate.g${i + 1}.chk`] = g.chk;
    });
    out.calc['c5.say'] = check5Say;
    out.calc.tranches = tranches;

    out.results = {
      items: total ? counts.join(', ') : '',
      gates: gates.some((g) => g.any) ? `${fullGates} of 3` : '',
      noOwner: total ? String(noOwner) : '',
      risks: riskRows.length ? `${risksFull} of 3` : '',
      guesses,
      check5: check5Say === '' ? '' : check5Say === 'Pass' ? 'Pass' : 'Go back',
    };

    // evidence: tranches and the contingency, when tagged E or G
    tr.forEach((t) => {
      if ((t.tag === 'E' || t.tag === 'G') && trText(t)) {
        out.evidence.push(gap(`Phase ${t.no} tranche: ${trText(t)}`, t.tag, `Canvas 8, Phase ${t.no} tranche`));
      }
    });
    const ctag = S(v, 'cont.tag');
    if ((ctag === 'E' || ctag === 'G') && contingency) {
      const why = S(v, 'cont.why');
      out.evidence.push(gap(`Contingency assumption: ${contingency}${why ? `: ${why}` : ''}`, ctag, 'Canvas 8, contingency'));
    }

    // warnings: advice only
    PHASES.forEach((ph, i) => {
      items[i].filter((r) => !r.owner).forEach((r) => out.warnings.push({ step: 's2', text: `Phase ${ph.no}: "${r.item}" has no owner. No owner, no slot.` }));
      LANES.forEach((lane) => {
        const n = items[i].filter((r) => r.lane === lane.v).length;
        if (n > 3) out.warnings.push({ step: 's2', text: `Phase ${ph.no}, ${lane.v}: ${n} items. The printed canvas holds three. Split one, or cut it.` });
      });
    });
    if (arr(fx(up, 6).launchConditions).length && counts[0] > 0 && !items[0].some((r) => r.lane === 'Governance')) {
      out.warnings.push({ step: 's2', text: 'Canvas 6 has launch conditions, and Phase 1 has no Governance item. Phase 1 holds the launch conditions.' });
    }
    if (arr(fx(up, 5).dataItems).some((d) => d.item && !d.owner) && counts[0] > 0 && !items[0].some((r) => r.lane === 'Data and platform')) {
      out.warnings.push({ step: 's2', text: 'Canvas 5 has data items with no owner, and Phase 1 has no Data and platform item. Each one is a Phase 1 dependency.' });
    }
    gates.forEach((g, i) => {
      if (g.decider && !oneRole(g.decider)) {
        out.warnings.push({ step: 's3', text: `Gate ${i + 1}: check the decider. One named role decides, not a committee, a board, a team or two people.` });
      }
    });
    if (isNum(inv.oneOffLow) && isNum(inv.oneOffHigh) && withNums.length && tr[2].low === null && tr[2].high === null) {
      const off = Math.abs(lows - inv.oneOffLow) > 0.5 || Math.abs(highs - inv.oneOffHigh) > 0.5;
      if (off) {
        const sums = `${mil(lows)} to ${mil(highs)} ${cur}M`;
        const cost = `${mil(inv.oneOffLow)} to ${mil(inv.oneOffHigh)}`;
        out.warnings.push({ step: 's3', text: `The tranches add to ${sums}. Canvas 4 has a one-off cost of ${cost}.` });
      }
    }
    tr.forEach((t) => {
      if (t.low !== null && t.high !== null && t.low > t.high) {
        out.blockers.push({ step: 's3', text: `Phase ${t.no} tranche: the low figure is above the high figure.` });
      }
      if (t.low !== null && t.low > 0 && t.low < 1000 && isNum(inv.oneOffHigh) && inv.oneOffHigh >= 10000) {
        out.warnings.push({ step: 's3', text: `Phase ${t.no} tranche: Canvas 4 uses full amounts, and this looks like millions. Type the full amount.` });
      }
    });
    top5.forEach((g, i) => {
      if (g.tag === 'G' && S(v, `tick.t${i + 1}.p1`) !== 'Yes') {
        out.warnings.push({ step: 's5', text: `"${g.item}" is a Guess with no Phase 1 item. Add a measurement task in step 2, then tick it.` });
      }
    });
    const c4holder = String(inv.contingencyHolder || '').trim();
    if (c4holder && S(v, 'cont.holder') && the(c4holder).toLowerCase() !== the(S(v, 'cont.holder')).toLowerCase()) {
      out.warnings.push({ step: 's5', text: `Canvas 4 says the contingency is held by ${the(c4holder)}. Change one of the two so they agree.` });
    }
    if (!pct) out.warnings.push({ step: 's5', text: 'Canvas 4 has no contingency percentage. Go back and set it, then say who holds it here.' });

    out.facts = {
      sponsor: fx(up, 0).sponsor || '',
      phases: PHASES.map((ph, i) => {
        const g = gates[i];
        const p = {
          no: ph.no,
          name: ph.name,
          window: ph.window,
          items: items[i].map((r) => ({ lane: r.lane || '', item: r.item, owner: r.owner || '' })),
        };
        if (trText(tr[i])) {
          p.tranche = trText(tr[i]);
          p.trancheTag = tr[i].tag;
        }
        if (g.any) {
          p.gate = {
            value: g.value,
            launch: g.launch,
            readiness: g.ready,
            decider: withThe(g.decider),
            releases: releases(i + 1),
            check: g.chk,
          };
        }
        return p;
      }),
      contingency,
      risks: riskRows.map((r) => ({ risk: r.risk || '', trigger: r.trigger || '', owner: r.owner || '', response: r.response || '' })),
      counts,
      stop: S(v, 'stop'),
      tranches,
      later: arr(up && up[3] && up[3].d && up[3].d.facts && up[3].d.facts.candidates)
        .filter((c) => c.name && c.pick !== 'top' && c.pick !== 'second' && S(v, `later.${c.id}.trigger`))
        .map((c) => ({ name: c.name, trigger: S(v, `later.${c.id}.trigger`) })),
      check5: out.results.check5,
    };
    return out;
  },
  NORTHSTAR,
});
